import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, CircularProgress, Divider, List, ListItemButton, ListItemText, Typography,
} from '@mui/material';
import { listSchemas, listTables, sampleCollection } from '../../api/connections';
import JsonViewer from '../common/JsonViewer';

const errorText = (err) => err.response?.data?.detail || err.message || 'Request failed';

// Databases -> collections (with estimated counts) -> sample documents for a MongoDB connection.
const MongoBrowser = ({ connection, onSelectCollection }) => {
  const connectionId = connection.id;
  const [databases, setDatabases] = useState([]);
  const [database, setDatabase] = useState(connection.database || null);
  const [collections, setCollections] = useState([]);
  const [collection, setCollection] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState({ dbs: true, colls: false, docs: false });
  const [error, setError] = useState('');

  const busy = (key, on) => setLoading((l) => ({ ...l, [key]: on }));

  useEffect(() => {
    let cancelled = false;
    setError('');
    busy('dbs', true);
    listSchemas(connectionId)
      .then((res) => { if (!cancelled) setDatabases(res.schemas || []); })
      .catch((err) => { if (!cancelled) setError(errorText(err)); })
      .finally(() => { if (!cancelled) busy('dbs', false); });
    return () => { cancelled = true; };
  }, [connectionId]);

  useEffect(() => {
    if (!database) { setCollections([]); return undefined; }
    let cancelled = false;
    setError('');
    setCollection(null);
    setDocuments(null);
    busy('colls', true);
    listTables(connectionId, { schema: database })
      .then((res) => { if (!cancelled) setCollections(res.tables || []); })
      .catch((err) => { if (!cancelled) { setCollections([]); setError(errorText(err)); } })
      .finally(() => { if (!cancelled) busy('colls', false); });
    return () => { cancelled = true; };
  }, [connectionId, database]);

  const openCollection = useCallback(async (name) => {
    setCollection(name);
    setDocuments(null);
    setError('');
    busy('docs', true);
    if (onSelectCollection) onSelectCollection(database, name);
    try {
      const res = await sampleCollection(connectionId, name, { database });
      setDocuments(res.documents || []);
    } catch (err) {
      setError(errorText(err));
    } finally {
      busy('docs', false);
    }
  }, [connectionId, database, onSelectCollection]);

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ width: 200, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary">Databases</Typography>
          {loading.dbs && <CircularProgress size={16} sx={{ ml: 1 }} />}
          <List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto' }} aria-label="Databases">
            {databases.map((name) => (
              <ListItemButton key={name} selected={name === database} onClick={() => setDatabase(name)}>
                <ListItemText primary={name} />
              </ListItemButton>
            ))}
            {!loading.dbs && databases.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No databases</Typography>
            )}
          </List>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ width: 240, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {database ? `Collections in ${database}` : 'Collections'}
          </Typography>
          {loading.colls && <CircularProgress size={16} sx={{ ml: 1 }} />}
          <List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto' }} aria-label="Collections">
            {collections.map((c) => (
              <ListItemButton key={c.name} selected={c.name === collection} onClick={() => openCollection(c.name)}>
                <ListItemText primary={c.name} secondary={`${(c.row_count ?? 0).toLocaleString()} docs`} />
              </ListItemButton>
            ))}
            {database && !loading.colls && collections.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No collections</Typography>
            )}
          </List>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {collection ? `Sample documents — ${collection}` : 'Select a collection to see sample documents'}
          </Typography>
          {loading.docs && <CircularProgress size={16} sx={{ ml: 1 }} />}
          {documents && (documents.length === 0
            ? <Typography variant="body2" color="text.secondary">Collection is empty</Typography>
            : <JsonViewer value={documents} maxHeight={360} defaultExpandDepth={2} />)}
        </Box>
      </Box>
    </Box>
  );
};

export default MongoBrowser;
